import { useSyncedCookie } from '@composables/useSyncedCookie';
import { StepsCompleted } from '@composables/useUserPreferences';
import { CookiesNames } from '@utils/consts';
import { getHoursAsSeconds } from '@utils/time';

import { useAuthStore } from '@stores/auth';

export type MyCookie<T> = T | null;

export type UserPreferencesCompletedCookie = MyCookie<number>;
export type UserPreferencesSkipCookie = MyCookie<boolean>;
export type UserPreferencesSkipCountCookie = MyCookie<number>;

const MAX_ATTEMPTS = 3;
const SKIP_RESET_INTERVAL_HOURS = 1;
const MAX_ATTEMPTS_RESET_HOURS = 24;

export const useUserPreferencesModal = async () => {
    const { runWithContext } = useNuxtApp();
    const authStore = useAuthStore();

    const { query } = useRoute();
    const { replace } = useRouter();

    const querySurveyValue = computed(() => {
        return getFirstIfArray(query.survey);
    });

    const cookieCompletedSteps =
        useSyncedCookie<UserPreferencesCompletedCookie>(
            CookiesNames.USER_PREFERENCES_STEPS_COMPLETED
        );
    const cookieSkip = useSyncedCookie<UserPreferencesSkipCookie>(
        CookiesNames.USER_PREFERENCES_SKIP
    );
    const cookieSkipCount = useSyncedCookie<UserPreferencesSkipCountCookie>(
        CookiesNames.USER_PREFERENCES_SKIP_COUNT
    );

    const setCookieCompletedSteps = (data: UserPreferencesCompletedCookie) => {
        cookieCompletedSteps.value = { value: data };
    };

    const setCookieSkip = () => {
        cookieSkip.value = {
            value: true,
            options: {
                maxAge: getHoursAsSeconds(SKIP_RESET_INTERVAL_HOURS),
            },
        };
    };

    const increaseCookieSkipCount = () => {
        if (isMaxAttemptsReached.value) return;
        cookieSkipCount.value = {
            value: (cookieSkipCount.value.value || 0) + 1,
            options: {
                maxAge: getHoursAsSeconds(MAX_ATTEMPTS_RESET_HOURS),
            },
        };
    };

    const cookieCompletedStepsValue = computed(() => {
        return cookieCompletedSteps.value?.value;
    });

    const cookieCompletedStepsValueDefault = computed(() => {
        return cookieCompletedStepsValue.value || 0;
    });

    const hasCookieCompletedSteps = computed(() => {
        return cookieCompletedStepsValue.value !== undefined;
    });

    const isInterestsSaved = computed(
        () =>
            cookieCompletedStepsValueDefault.value === StepsCompleted.Interests
    );

    const allPreferencesSaved = computed(
        () =>
            cookieCompletedStepsValueDefault.value === StepsCompleted.Frequency
    );

    const startStep = computed(() => {
        if (isPreferencesQuerySurvey.value) return 1;
        return isInterestsSaved.value ? 2 : 1;
    });

    const isMaxAttemptsReached = computed(
        () =>
            cookieSkipCount.value?.value &&
            cookieSkipCount.value.value >= MAX_ATTEMPTS
    );

    const isSkipped = computed(() => {
        return cookieSkip.value?.value;
    });

    const isPreferencesQuerySurvey = computed(() => {
        return querySurveyValue.value === 'preferences';
    });

    const removePreferencesSurveyQuery = async () => {
        if (!isPreferencesQuerySurvey.value) return;
        const newQueryParams = { ...query };
        delete newQueryParams.survey;
        await replace({ query: newQueryParams });
    };

    const shouldDisplay = computed(() => {
        // has survey=preferences query parameter - display
        if (isPreferencesQuerySurvey.value) return true;
        // all saved - don't display
        if (allPreferencesSaved.value) return false;
        // skiped 3 times - stop displaying for 24 hours
        if (isMaxAttemptsReached.value) return false;
        // skiped - stop displaying for an hour
        return !isSkipped.value;
    });

    const shouldFetch = computed(() => {
        return (
            shouldDisplay.value &&
            (isInterestsSaved.value ||
                !hasCookieCompletedSteps.value ||
                isPreferencesQuerySurvey.value)
        );
    });

    const isFetched = ref(false);

    const computedShouldDisplay = computed(() => {
        if (shouldFetch.value) return isFetched.value;
        return shouldDisplay.value;
    });

    if (shouldFetch.value) {
        const data = await authStore.getUserPreferences();

        if (data) {
            const frequencySaved = !!data.attendanceFrequency;
            const interestsSaved = !!data.interests;
            await runWithContext(() =>
                setCookieCompletedSteps(
                    frequencySaved && interestsSaved
                        ? StepsCompleted.Frequency
                        : StepsCompleted.Interests
                )
            );
        } else {
            await runWithContext(() =>
                setCookieCompletedSteps(StepsCompleted.None)
            );
        }
        isFetched.value = true;
    }

    return {
        computedShouldDisplay,
        startStep,
        setCookieCompletedSteps,
        setCookieSkip,
        increaseCookieSkipCount,
        removePreferencesSurveyQuery,
    };
};
